/**
 * Remove the secret of a reset or verify link, and the text of a search box,
 * from a telemetry item (#452, #595).
 *
 * The link carries its token in `?token=`. A list keeps its search text in
 * `?q=` (#595), and the text is often the name of a person. The SDK records
 * a page view for each `history.replaceState`, so each change of the text
 * was a page view with that text. The SDK records the URL of every
 * page view, the referrer of the NEXT page view, and the URL of every API
 * call — `VerifyEmail` sends the token to the API in the query string too.
 * The app adds its own copies as custom properties: `http_retry_succeeded`,
 * the exception of a 5xx and `logBackgroundError()` all send the raw axios
 * URL as `url`. The message and the stack of an exception are text too, also
 * for a promise that rejects with no catch (#679).
 *
 * The SDK's own redaction knows only `sig` and three cloud-storage names,
 * and a custom list reaches neither the XHR path that axios uses nor the
 * page-view URL (read in SDK 3.4.4), so this runs as a telemetry initializer
 * on every item instead.
 *
 * A pure module with no Vite syntax, so `privacy-452.spec.ts` imports it.
 */

/** The query parameters whose value never goes into telemetry: a credential
 *  (`token`) and the text that a person types in a search box (`q` of the
 *  lists, `search` of the audit log of the admin page). */
export const REDACTED_QUERY_PARAMS = ['token', 'q', 'search'] as const;

// The name follows `?`, `&`, white space or the start of the string.
// The value ends at `&`, `#`, a quote (`"`) or the end of the line.
// A `'` or a `)` does not end it, because `encodeURIComponent` leaves both
// unescaped ("q=O'Brien", "search=Jonas%20(Tiger)%20Jonaitis").
// White space does not end it: a decoded search text in a message
// ("q=Jonas Jonaitis") has spaces.
// Accepted edge: a decoded search text that holds `&` or `#` ends the value
// early ("q=Jonas & Jonaitis" keeps " Jonaitis"). A name stays in the text.
// A value never crosses a line, so the next lines of a stack stay.
const REDACTED_PARAM_RE = new RegExp(`((?:^|[?&\\s])(?:${REDACTED_QUERY_PARAMS.join('|')})=)[^&#"\\r\\n]*`, 'gi');

/** Replace the value of every redacted query parameter in one string. */
export function scrubUrl(value: string): string {
  return value.replace(REDACTED_PARAM_RE, '$1REDACTED');
}

type Bag = Record<string, unknown>;
const isBag = (v: unknown): v is Bag => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Scrub every string value of one bag, in place. */
function scrubStrings(bag: unknown): void {
  if (!isBag(bag)) return;
  for (const [key, value] of Object.entries(bag)) {
    if (typeof value === 'string') bag[key] = scrubUrl(value);
  }
}

/** Scrub every string of the bags that reach the envelope, in place:
 *  Part B (`baseData`, and its `properties`, where a page view keeps its
 *  own) and Part C (`data`, where the SDK puts the custom properties of an
 *  event and of an exception). Every string, not a list of URL field names:
 *  the first version listed names, and the custom `url` property in Part C
 *  was outside it.
 *
 *  The text of an exception is in none of these bags. It stands in
 *  `baseData.exceptions[]`: the `typeName`, the `message` and the `stack` of
 *  each entry (#679). The SDK tracks a promise that rejects with no catch, and
 *  a message can hold a URL with a token or a search text. The frames in
 *  `parsedStack` have a `fileName` and an `assembly`. Both are scrubbed too. */
export function scrubTelemetryUrls(item: { baseData?: Bag; data?: Bag }): void {
  for (const bag of [item.baseData, item.baseData?.properties, item.data]) scrubStrings(bag);
  const exceptions = item.baseData?.exceptions;
  if (Array.isArray(exceptions)) {
    for (const exception of exceptions) {
      scrubStrings(exception);
      // A frame names a script file, and a file name can hold a query.
      const frames = isBag(exception) ? exception.parsedStack : null;
      if (Array.isArray(frames)) frames.forEach(scrubStrings);
    }
  }
}

/** The path of a URL, or null for a value that is no URL. */
function pathOf(url: unknown): string | null {
  if (typeof url !== 'string' || url === '') return null;
  try {
    return new URL(url, 'http://localhost').pathname;
  } catch {
    return null;
  }
}

/**
 * #595 review — the route tracking of the SDK sends a page view for each
 * `history.replaceState`, and a list writes its search text and its filters
 * into the URL on each change: one page view for each key. A page view on
 * the path of the page view before it changes the query only: it is no new
 * page, and the saved queries that count `AppPageViews` must not count it.
 * Returns whether to drop the item, and the path of the last page view.
 */
export function queryOnlyPageView(item: { baseType?: string; baseData?: Bag }, lastPath: string | null): { drop: boolean; lastPath: string | null } {
  if (item.baseType !== 'PageviewData') return { drop: false, lastPath };
  const path = pathOf(item.baseData?.uri);
  if (path === null) return { drop: false, lastPath };
  return { drop: path === lastPath, lastPath: path };
}
